import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Above from "core-web/components/Breakpoints/Above";
import Below from "core-web/components/Breakpoints/Below";
import Link from "core-web/components/Link";
import ScrollableBox from "core-web/components/ScrollableBox";
import Slider from "core-web/components/sliders/Slider";
import styled from "theme/libs/styled";
import ArrowIcon from "theme/components/icons/ArrowIcon";
import ProductCard from "theme/components/products/ProductCard";
import SkeletonProductCard from "theme/components/skeletons/ProductCard";
import TextStyles from "theme/components/text/TextStyles";
import colors from "theme/config/colors";

const Wrapper = styled("div")`
    overflow: hidden;
`;

const SliderWrapper = styled("div")`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const ButtonsWrapper = styled("div")`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const ArrowButton = styled("button")`
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    pointer-events: ${({ active }) => (active ? "auto" : "none")};
`;

const Header = styled("div")`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-bottom: 38px;
    padding-top: 24px;

    ${({ theme }) => theme.above.tabletSm} {
        padding-bottom: 24px;
        align-items: center;
        flex-direction: row;
    }
`;

const RightSide = styled("div")`
    display: flex;
    align-items: center;
    padding-top: 16px;

    ${({ theme }) => theme.below.tabletSm} {
        &[data-has-link="false"] {
            display: none;
        }
    }

    ${({ theme }) => theme.above.tabletSm} {
        padding: 0;
    }
`;

const MaskedScrollableBox = styled(ScrollableBox)`
    display: flex;
    width: 100%;
`;

const ProductWrapper = styled("div")`
    flex: 0 0 auto;
    box-sizing: border-box;

    &:last-of-type {
        margin: 0;
    }
`;

const ProductSlider = ({
    products,
    productsAmount = 4,
    headingText,
    headingTag,
    url,
    linkText,
    headingTextStyles = ["Myriad Pro/32-120-semibold", null, null, null, "Myriad Pro/40-100-semibold"],
    slidesPerView,
    paddingX,
    productCardStyles,
    spaceBetween,
    isRecommendedSlider,
    depictSessionId,
    userId,
    shouldLazyLoad,
    ...rest
}) => {
    const slider = useRef(null);

    const [sliderPos, setSliderPos] = useState("start");
    const [slideIndex, setSlideIndex] = useState(0);

    const emptyItemsArray = Array.from({ length: productsAmount });
    const numberOfChildren = products?.length || productsAmount || 4;

    const slideTo = (index) => {
        if (index < 0) {
            index = 0;
        }

        if (index > -1 && index <= numberOfChildren) {
            requestAnimationFrame(() => {
                setSlideIndex(index + slidesPerView > numberOfChildren ? numberOfChildren - slidesPerView : index);
            });
        }
    };

    const slidePrev = () => {
        slideTo(slideIndex - slidesPerView);
    };

    const slideNext = () => {
        slideTo(slideIndex + slidesPerView);
    };

    useEffect(() => {
        if (slideIndex === 0) {
            setSliderPos("start");
        } else if (slideIndex + slidesPerView >= numberOfChildren) {
            setSliderPos("end");
        } else {
            setSliderPos("between");
        }
    }, [slideIndex, slidesPerView, numberOfChildren]);

    return (
        <Wrapper {...rest}>
            <Header px={paddingX}>
                <TextStyles as={headingTag} textStyles={headingTextStyles}>
                    {headingText}
                </TextStyles>
                <RightSide data-has-link={!!url}>
                    {!!url && (
                        <Link to={url} mr={[null, null, null, null, "8px"]}>
                            <TextStyles
                                color={colors.black_70}
                                as="span"
                                textStyles={["Myriad Pro/16-100", null, null, null, "Myriad Pro/18-120"]}
                            >
                                {linkText}
                            </TextStyles>
                            <Below
                                breakpoint="tabletLg"
                                render={() => (
                                    <ArrowIcon
                                        direction="right"
                                        width="14px"
                                        height="14px"
                                        color={colors.black_70}
                                        ml="8px"
                                    />
                                )}
                            />
                        </Link>
                    )}
                    {numberOfChildren > slidesPerView && (
                        <Above
                            breakpoint="tabletLg"
                            render={() => (
                                <ButtonsWrapper ml="24px">
                                    <ArrowButton
                                        active={sliderPos !== "start" && numberOfChildren >= slidesPerView}
                                        onClick={slidePrev}
                                    >
                                        <ArrowIcon
                                            color={colors.black_70}
                                            direction="left"
                                            width="14px"
                                            height="14px"
                                        />
                                    </ArrowButton>

                                    <ArrowButton
                                        active={sliderPos !== "end" && numberOfChildren >= slidesPerView}
                                        onClick={slideNext}
                                    >
                                        <ArrowIcon
                                            color={colors.black_70}
                                            direction="right"
                                            width="14px"
                                            height="14px"
                                        />
                                    </ArrowButton>
                                </ButtonsWrapper>
                            )}
                        />
                    )}
                </RightSide>
            </Header>
            <Below
                breakpoint="tabletLg"
                render={() => (
                    <MaskedScrollableBox scrollDirection="horizontal" px={paddingX}>
                        {(products?.length ? products : emptyItemsArray).map((product, i) => (
                            <ProductWrapper key={i} mr={spaceBetween} {...productCardStyles}>
                                {product ? (
                                    <ProductCard
                                        depictSessionId={depictSessionId}
                                        userId={userId}
                                        isRecommendedCard={isRecommendedSlider}
                                        {...product}
                                        key={i}
                                    />
                                ) : products?.length ? null : (
                                    <SkeletonProductCard key={i} />
                                )}
                            </ProductWrapper>
                        ))}
                    </MaskedScrollableBox>
                )}
            />
            <Above
                breakpoint="tabletLg"
                render={() => (
                    <SliderWrapper px={paddingX}>
                        <Slider
                            width="100%"
                            spaceBetween={
                                Array.isArray(spaceBetween) ? spaceBetween[spaceBetween.length - 1] : spaceBetween
                            }
                            ref={slider}
                            slidesPerView={slidesPerView}
                            activeSlideIndex={slideIndex}
                            onSlideChange={setSlideIndex}
                        >
                            {(products?.length ? products : emptyItemsArray).map((product, index) =>
                                product ? (
                                    <ProductCard
                                        key={product.partNo + index}
                                        depictSessionId={depictSessionId}
                                        userId={userId}
                                        isRecommendedCard={isRecommendedSlider}
                                        loading={!shouldLazyLoad && index < slidesPerView ? "eager" : "lazy"}
                                        {...product}
                                    />
                                ) : products?.length ? null : (
                                    <SkeletonProductCard key={index} />
                                ),
                            )}
                        </Slider>
                    </SliderWrapper>
                )}
            />
        </Wrapper>
    );
};

ProductSlider.propTypes = {
    depictSessionId: PropTypes.string,
    headingText: PropTypes.string,
    headingTextStyles: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    headingTag: PropTypes.string,
    isRecommendedSlider: PropTypes.bool,
    linkText: PropTypes.string,
    paddingX: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    productCardStyles: PropTypes.object,
    products: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            price: PropTypes.object,
        }),
    ).isRequired,
    productsAmount: PropTypes.number,
    shouldLazyLoad: PropTypes.bool,
    slidesPerView: PropTypes.number,
    spaceBetween: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    type: PropTypes.string,
    url: PropTypes.string,
    userId: PropTypes.string,
};

ProductSlider.defaultProps = {
    depictSessionId: null,
    headingText: "",
    headingTextStyles: ["Myriad Pro/32-100-semibold", null, null, null, "Myriad Pro/40-100-semibold"],
    headingTag: "h2",
    isRecommendedSlider: false,
    linkText: "",
    paddingX: ["20px", null, null, "48px"],
    productCardStyles: { width: ["70%", null, "45%", "30%"] },
    productsAmount: 0,
    shouldLazyLoad: true,
    slidesPerView: 4,
    spaceBetween: ["12px", null, null, "16px"],
    type: "",
    url: "",
    userId: null,
};

export default ProductSlider;
